import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  pacientesService as pacienteService, 
  consultasService as consultaService
} from '../services/api';
import type { Consulta } from '../types';
import type { Paciente } from '../services/api/pacientesService';
import type { ResumoMensal } from '../types/consulta';
import { 
  formatarTelefone, 
  formatarCPF, 
  formatarData, 
  formatarMoeda 
} from '../utils/formatters';
import { authService } from '../services/authService'; // CORREÇÃO: Import correto

export default function DetalhesPaciente() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [paciente, setPaciente] = useState<Paciente | null>(null);
  const [proximasConsultas, setProximasConsultas] = useState<Consulta[]>([]);
  const [consultasRealizadas, setConsultasRealizadas] = useState<Consulta[]>([]);
  const [pagamentosPendentes, setPagamentosPendentes] = useState<Consulta[]>([]);
  const [resumoFinanceiro, setResumoFinanceiro] = useState<ResumoMensal[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  
  // Carregar dados do paciente e consultas COM VALIDAÇÃO
  useEffect(() => {
    async function carregarDados() {
      if (!id) return;
      
      try {
        setCarregando(true);
        setErro(null);
        
        // VALIDAÇÃO CRÍTICA: Verificar autenticação
        if (!authService.isAuthenticated()) {
          console.error('[DetalhesPaciente] Usuário não autenticado');
          navigate('/login');
          return;
        }
        
        const dadosPaciente = await pacienteService.obterPorId(id);
        
        if (dadosPaciente) {
          setPaciente(dadosPaciente);
          
          // Carregar consultas do paciente
          const filtrosConsulta = {
            pacienteId: id,
            incluirCanceladas: true
          };
          
          console.log('[DetalhesPaciente] Carregando consultas com filtros:', filtrosConsulta);
          
          const todasConsultas = await consultaService.listarTodas(filtrosConsulta);
          
          // Validar se as consultas pertencem ao paciente correto
          const consultasValidadas = todasConsultas.filter((c: Consulta) => c.pacienteId === id);
          
          if (consultasValidadas.length !== todasConsultas.length) {
            console.warn('[DetalhesPaciente] Algumas consultas filtradas por segurança');
          }
          
          // Processar consultas do paciente
          const hoje = new Date().toISOString().split('T')[0];
          
          const proximas = consultasValidadas.filter(
            (c: Consulta) => c.data && c.data >= hoje && c.realizada === false
          ).sort((a: Consulta, b: Consulta) => 
            (a.data || '').localeCompare(b.data || '') || 
            (a.hora || '').localeCompare(b.hora || '')
          );
          
          const realizadas = consultasValidadas.filter(
            (c: Consulta) => c.realizada === true
          ).sort((a: Consulta, b: Consulta) => 
            (b.data || '').localeCompare(a.data || '')
          );
          
          const pendentes = consultasValidadas.filter(
            (c: Consulta) => c.realizada === true && c.pagamentoRecebido === false
          ).sort((a: Consulta, b: Consulta) => 
            (a.data || '').localeCompare(b.data || '')
          );
          
          // Calcular resumo financeiro mensal
          const resumoPorMes: Record<string, ResumoMensal> = {};
          
          consultasValidadas.forEach((consulta: Consulta) => {
            if (!consulta.data) return;
            
            const data = new Date(consulta.data);
            const mes = `${String(data.getMonth() + 1).padStart(2, '0')}/${data.getFullYear()}`;
            
            if (!resumoPorMes[mes]) {
              resumoPorMes[mes] = {
                mes,
                recebido: 0,
                pendente: 0,
                total: 0
              };
            }
            
            if (consulta.realizada) {
              if (consulta.pagamentoRecebido) {
                resumoPorMes[mes].recebido += consulta.valor;
              } else {
                resumoPorMes[mes].pendente += consulta.valor;
              }
              resumoPorMes[mes].total += consulta.valor;
            }
          });
          
          const resumoArray = Object.values(resumoPorMes).sort((a, b) => {
            const [mesA, anoA] = a.mes.split('/');
            const [mesB, anoB] = b.mes.split('/');
            
            if (anoA !== anoB) return Number(anoB) - Number(anoA);
            return Number(mesB) - Number(mesA);
          });
          
          setProximasConsultas(proximas);
          setConsultasRealizadas(realizadas);
          setPagamentosPendentes(pendentes);
          setResumoFinanceiro(resumoArray);
        } else {
          setErro('Paciente não encontrado');
          setCarregando(false);
          return;
        }
        
      } catch (error) {
        console.error('Erro ao carregar dados do paciente:', error);
        
        if (error && typeof error === 'object' && 'response' in error && 
            error.response && typeof error.response === 'object' && 
            'status' in error.response && error.response.status === 401) {
          console.error('[DetalhesPaciente] Erro 401 - redirecionando');
          authService.logout();
          navigate('/login');
          return;
        }
        
        setErro('Não foi possível carregar os dados. Tente novamente mais tarde.');
      } finally {
        setCarregando(false);
      }
    }
    
    carregarDados();
  }, [id, navigate]);
  
  if (carregando) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border"></div>
        <p>Carregando informações do paciente...</p>
      </div>
    );
  }
  
  if (erro || !paciente) {
    return (
      <div className="alert alert-danger">
        <h4 className="alert-heading">Erro ao carregar dados</h4>
        <p>{erro || 'Paciente não encontrado'}</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Voltar para lista
        </button>
      </div>
    );
  }
  
  const p = paciente;
  
  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Detalhes do Paciente</h2>
        <div>
          <Link to="/" className="btn btn-outline-secondary me-2">
            Voltar
          </Link>
          <Link to={`/pacientes/${id}/editar`} className="btn btn-primary me-2">
            Editar
          </Link>
          {authService.getUsuario()?.cargo !== 'atendente' && (
            <Link to={`/pacientes/${id}/prontuario?source=paciente&pacienteId=${id}`} className="btn btn-success">
              Prontuário
            </Link>
          )}
        </div>
      </div>
      
      {/* Dados do Paciente */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h3 className="card-title h5 mb-0">Informações Pessoais</h3>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <p><strong>Nome:</strong> {p.nome || '-'}</p>
              <p><strong>Serviço:</strong> {p.servico === 'triagem' ? 'Triagem' : 'Psicoterapia'}</p>
              <p><strong>CPF:</strong> {formatarCPF(p.cpf)}</p>
              <p><strong>Profissão:</strong> {p.profissao || '-'}</p>
              <p><strong>Endereço:</strong> {p.endereco || '-'}</p>
              {p.dataPrimeiraConsulta && (
                <p><strong>Primeira Consulta:</strong> {formatarData(p.dataPrimeiraConsulta)}</p>
              )}
            </div>
            <div className="col-md-6">
              <p><strong>E-mail:</strong> {p.email || '-'}</p>
              <p><strong>Telefone:</strong> {formatarTelefone(p.telefone)}</p>
              <p><strong>Contato de Emergência:</strong> {p.nome_emergencia || '-'} - {formatarTelefone(p.telefone_emergencia)}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Resumo Financeiro */}
      <div className="card mb-4">
        <div className="card-header bg-success text-white">
          <h3 className="card-title h5 mb-0">Resumo Financeiro por Mês</h3>
        </div>
        <div className="card-body">
          {resumoFinanceiro.length === 0 ? (
            <p className="text-muted">Nenhuma consulta realizada até o momento.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Mês/Ano</th>
                    <th>Valor Recebido</th>
                    <th>Valor Pendente</th>
                    <th>Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  {resumoFinanceiro.map((mes, index) => (
                    <tr key={index}>
                      <td>{mes.mes}</td>
                      <td className="text-success">{formatarMoeda(mes.recebido)}</td>
                      <td className="text-warning">{formatarMoeda(mes.pendente)}</td>
                      <td>{formatarMoeda(mes.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Próximas Consultas */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h3 className="card-title h5 mb-0">Próximas Consultas</h3>
        </div>
        <div className="card-body">
          {proximasConsultas.length === 0 ? (
            <p className="text-muted">Não há consultas agendadas.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Hora</th>
                    <th>Valor</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {proximasConsultas.map(consulta => (
                    <tr key={consulta.id}>
                      <td>{formatarData(consulta.data)}</td>
                      <td>{consulta.hora}</td>
                      <td>{formatarMoeda(consulta.valor)}</td>
                      <td>
                        <Link 
                          to={`/consultas/${consulta.id}/editar`}
                          className="btn btn-sm btn-outline-primary"
                        >
                          Detalhes
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <div className="mt-3">
            <Link 
              to={`/consultas/nova?pacienteId=${id}`} 
              className="btn btn-success"
            >
              Agendar Nova Consulta
            </Link>
          </div>
        </div>
      </div>
      
      {/* Pagamentos Pendentes */}
      <div className="card mb-4">
        <div className="card-header bg-warning">
          <h3 className="card-title h5 mb-0">Pagamentos Pendentes</h3>
        </div>
        <div className="card-body">
          {pagamentosPendentes.length === 0 ? (
            <p className="text-muted">Não há pagamentos pendentes.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Hora</th>
                    <th>Valor</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {pagamentosPendentes.map(consulta => (
                    <tr key={consulta.id}>
                      <td>{formatarData(consulta.data)}</td>
                      <td>{consulta.hora}</td>
                      <td>{formatarMoeda(consulta.valor)}</td>
                      <td>
                        <Link 
                          to={`/consultas/${consulta.id}/editar`}
                          className="btn btn-sm btn-outline-primary"
                        >
                          Registrar Pagamento
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 font-weight-bold">
                Total pendente: {formatarMoeda(pagamentosPendentes.reduce((total, c) => total + c.valor, 0))}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Histórico de Consultas */}
      <div className="card mb-4">
        <div className="card-header bg-info text-white">
          <h3 className="card-title h5 mb-0">Histórico de Consultas</h3>
        </div>
        <div className="card-body">
          {consultasRealizadas.length === 0 ? (
            <p className="text-muted">Nenhuma consulta realizada até o momento.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Hora</th>
                    <th>Valor</th>
                    <th>Pagamento</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {consultasRealizadas.map(consulta => (
                    <tr key={consulta.id}>
                      <td>{formatarData(consulta.data)}</td>
                      <td>{consulta.hora}</td>
                      <td>{formatarMoeda(consulta.valor)}</td>
                      <td>
                        {consulta.pagamentoRecebido ? (
                          <span className="badge bg-success">Pago</span>
                        ) : (
                          <span className="badge bg-warning">Pendente</span>
                        )}
                      </td>
                      <td>
                        <Link 
                          to={`/consultas/${consulta.id}/editar`}
                          className="btn btn-sm btn-outline-primary"
                        >
                          Detalhes
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
